import {Component, inject, OnInit, output, signal} from '@angular/core';
import {TaxInformationApi} from './tax-information-api';
import {MessageService} from '../../../shared/services/message-service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ITaxInformationResponse} from './models/i-tax-information-response';
import {ProfileSync} from '../../../auth/services/profile-sync';
import {Loading} from '../../../shared/loading/loading';

@Component({
  selector: 'app-billing',
  imports: [
    ReactiveFormsModule,
    Loading
  ],
  templateUrl: './billing.html',
  styleUrl: './billing.scss',
})
export class Billing implements OnInit {
  taxInformationApi: TaxInformationApi = inject(TaxInformationApi);
  messageService: MessageService = inject(MessageService);
  fb: FormBuilder = inject(FormBuilder);
  profileSync: ProfileSync = inject(ProfileSync);

  saved = output<void>();

  form!: FormGroup;
  loading: boolean = false;
  taxInformation: ITaxInformationResponse | null | undefined = this.profileSync.user()?.user.taxInformation;

  ngOnInit() {
    this.initForm();
    if (this.taxInformation) {
      this.patchFormValues();
    }
  }

  private initForm() {
    this.form = this.fb.group({
      isCompany: [false, Validators.required],
      name: [''],
      surname: [''],
      companyName: [''],
      address: ['', Validators.required],
      houseNumber: [''],
      zipCode: ['', [Validators.required, Validators.pattern('^[0-9]{5}$')]],
      city: ['', Validators.required],
      province: ['', [Validators.required, Validators.maxLength(2), Validators.minLength(2)]],
      taxCode: ['', Validators.required],
      vatNumber: [''],
      sdiCode: ['', [Validators.pattern('^[a-zA-Z0-9]{7}$')]],
      pecAddress: ['']
    });

    // Imposta lo stato iniziale dei validatori
    this.updateValidators(this.form.get('isCompany')?.value);

    // Ascolta i cambiamenti su isCompany per aggiornare i validatori dinamici
    this.form.get('isCompany')?.valueChanges.subscribe(isCompany => {
      this.updateValidators(isCompany);
    });
  }

  private patchFormValues() {
    if (!this.taxInformation) return;
    this.form.patchValue({
      isCompany: this.taxInformation.isCompany,
      name: this.taxInformation.name,
      surname: this.taxInformation.surname,
      companyName: this.taxInformation.companyName,
      address: this.taxInformation.address,
      houseNumber: this.taxInformation.houseNumber,
      zipCode: this.taxInformation.zipCode,
      city: this.taxInformation.city,
      province: this.taxInformation.province,
      taxCode: this.taxInformation.taxCode,
      vatNumber: this.taxInformation.vatNumber,
      sdiCode: this.taxInformation.sdiCode,
      pecAddress: this.taxInformation.pecAddress
    });
  }

  private updateValidators(isCompany: boolean) {
    const name = this.form.get('name');
    const surname = this.form.get('surname');
    const companyName = this.form.get('companyName');
    const vatNumber = this.form.get('vatNumber');
    const pecAddress = this.form.get('pecAddress');
    const sdiCode = this.form.get('sdiCode');

    if (isCompany) {
      companyName?.setValidators([Validators.required]);
      vatNumber?.setValidators([Validators.required]);
      sdiCode?.setValidators([Validators.pattern('^[a-zA-Z0-9]{7}$')]);
      pecAddress?.setValidators([Validators.email]);

      name?.clearValidators();
      surname?.clearValidators();
      name?.setValue('');
      surname?.setValue('');
    } else {
      name?.setValidators([Validators.required]);
      surname?.setValidators([Validators.required]);

      companyName?.clearValidators();
      vatNumber?.clearValidators();
      sdiCode?.clearValidators();
      pecAddress?.clearValidators();

      companyName?.setValue('');
      vatNumber?.setValue('');
      sdiCode?.setValue('');
      pecAddress?.setValue('');
    }

    [name, surname, companyName, vatNumber, sdiCode, pecAddress].forEach(c => c?.updateValueAndValidity());
  }

  save() {
    if (this.form.invalid) return;

    this.loading = true;
    const obs$ = !!this.taxInformation
      ? this.taxInformationApi.update(this.form.value)
      : this.taxInformationApi.create(this.form.value);

    obs$.subscribe({
      next: data => {
        this.messageService.showSuccess('Dati di fatturazione salvati con successo');
        this.profileSync.updateTaxInformation(data);
        this.taxInformation = data;
        this.loading = false;
        this.saved.emit();
        this.form.markAsPristine();
      },
      error: err => {
        this.loading = false;
        this.messageService.handleError(err);
      }
    });
  }
}
